import { getCurrentLanguage } from '@gympass/i18n-language-web';
import * as ct from 'countries-and-timezones';
import { Cookies } from 'react-cookie';

import { LOCALE_COOKIE } from '../constants';
import { currencyByCountry, getAvailableCountries } from '../data';
import type { Country, Language } from '../types';

export const cookies = new Cookies();

export function isValidGympassCountry(country: string): country is Country {
  return getAvailableCountries().includes(country as Country);
}

const DEFAULT_COUNTRY = 'br' satisfies Country;

export function resolveCountry(): Country {
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();

  /*
  There is a relationship of one UTC offset (i. e.: UTC-4) to many countries, however, browser timezone
  often also includes the city. For instance: America/New_York (us), America/Toronto (ca): both UTC-4.
  allowing the library to infer the country yielded good results.
  */
  const country = ct.getCountryForTimezone(timeZone);

  if (!country) return DEFAULT_COUNTRY;

  const inferredCountry = country.id.toLowerCase();

  if (isValidGympassCountry(inferredCountry)) return inferredCountry;

  return DEFAULT_COUNTRY;
}

export function resolveCurrency(country?: Country) {
  if (!country) {
    return undefined;
  }

  return currencyByCountry[country];
}

// This app's LANGUAGES_BY_COUNTRY only offers de/it/ro as bare codes —
// partners-react-forms' LanguageSelector resolves an unmatched regional
// variant of these to its built-in en-US fallback rather than the correct
// country, so any of these values must alias down to the bare code before
// they ever reach it.
const LANGUAGE_ALIASES: Partial<Record<string, Language>> = {
  'ro-RO': 'ro',
  'de-DE': 'de',
  'it-IT': 'it',
};

export function getLanguage(): Language {
  const browserLanguage = getCurrentLanguage();
  const locale = cookies.get(LOCALE_COOKIE);

  const resolved = locale?.replace('_', '-') ?? browserLanguage ?? 'en';

  return LANGUAGE_ALIASES[resolved] ?? resolved;
}

export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Resolves the `common.language.*` i18n key suffix for an opportunity's
 * contract language. Multi-language countries have suffixed keys
 * (e.g. `ca` and `ca-fr`), so the country+language variant is tried first.
 */
export function getContractLanguageKey(
  countryCode: Country,
  language: Language | undefined,
  i18n: { exists: (key: string) => boolean },
): string {
  const countryLower = countryCode.toLowerCase();

  if (language) {
    const [primary = language] = language.split('-');
    const langPrefix = primary.toLowerCase();
    const candidate = `${countryLower}-${langPrefix}`;

    if (i18n.exists(`common.language.${candidate}`)) {
      return candidate;
    }
  }

  return countryLower;
}
